import React, { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { Reveal, Button } from "../components/ui";
import { PageHero } from "./About";
import SEO from "../components/SEO";
import { Calculator as CalcIcon, CheckCircle2, Loader2 } from "lucide-react";

export default function Calculator() {
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({ area: "", package_id: "", floors: 1, city: "", name: "", phone: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/packages").then((r) => {
      setPackages(r.data || []);
      const pop = (r.data || []).find((p) => p.popular) || r.data?.[0];
      if (pop) setForm((f) => ({ ...f, package_id: pop.id }));
    });
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const compute = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.area || !form.package_id) { setError("Please enter area and select a package."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/calculate", { ...form, area: parseFloat(form.area), floors: parseInt(form.floors) });
      setResult(data);
    } catch (err) { setError(formatError(err.response?.data?.detail)); } finally { setLoading(false); }
  };

  const inputCls = "w-full rounded-xl border border-line bg-white px-4 py-3 text-[15px] outline-none focus:border-gold";

  return (
    <>
      <SEO title="Construction Cost Calculator" description="Estimate your home construction cost in Indore with GNONS." />
      <PageHero kicker="Estimate" title="Construction Cost Calculator" subtitle="Get an indicative estimate for your construction project." />

      <section className="py-16 md:py-20 bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-10 items-start">
          <Reveal>
            <form onSubmit={compute} className="rounded-xl2 border border-line bg-sand p-6 md:p-8 space-y-5" data-testid="calculator-form">
              <div className="flex items-center gap-3 mb-2"><CalcIcon className="text-gold" size={26} /><h2 className="text-xl font-bold text-ink">Enter Details</h2></div>
              <div>
                <label className="text-sm font-semibold text-ink">Construction Area (sq.ft.)</label>
                <input data-testid="calc-area" className={inputCls + " mt-2"} type="number" inputMode="numeric" placeholder="e.g. 1500" value={form.area} onChange={set("area")} required />
              </div>
              <div>
                <label className="text-sm font-semibold text-ink">Package</label>
                <div className="grid gap-3 mt-2">
                  {packages.map((p) => (
                    <label key={p.id} data-testid={`calc-pkg-${p.id}`} className={`flex items-center justify-between gap-3 rounded-xl border p-4 cursor-pointer transition-all ${form.package_id === p.id ? "border-gold bg-gold/5" : "border-line bg-white"}`}>
                      <div className="flex items-center gap-3">
                        <input type="radio" name="pkg" checked={form.package_id === p.id} onChange={() => setForm({ ...form, package_id: p.id })} className="accent-gold" />
                        <div><div className="font-bold text-ink">{p.name}{p.popular && <span className="ml-2 text-[10px] bg-gold text-white px-2 py-0.5 rounded-full">POPULAR</span>}</div><div className="text-xs text-muted">₹{p.rate} {p.unit}</div></div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm font-semibold text-ink">Floors</label><input data-testid="calc-floors" className={inputCls + " mt-2"} type="number" min="1" value={form.floors} onChange={set("floors")} /></div>
                <div><label className="text-sm font-semibold text-ink">City</label><input className={inputCls + " mt-2"} placeholder="Indore" value={form.city} onChange={set("city")} /></div>
              </div>
              <div className="pt-2 border-t border-line grid grid-cols-2 gap-4">
                <input data-testid="calc-name" className={inputCls} placeholder="Your Name" value={form.name} onChange={set("name")} />
                <input data-testid="calc-phone" className={inputCls} placeholder="Mobile" type="tel" value={form.phone} onChange={set("phone")} />
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <button type="submit" data-testid="calc-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-white font-semibold py-3.5 shadow-gold hover:bg-gold-dark disabled:opacity-70">
                {loading ? <><Loader2 className="animate-spin" size={18} /> Calculating...</> : "Calculate Estimate"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={100}>
            <div className="rounded-xl2 border border-line p-8 bg-ink text-white sticky top-24">
              <h2 className="text-lg font-bold mb-6">Your Estimate</h2>
              {result ? (
                <div data-testid="calc-result">
                  <div className="text-white/60 text-sm">Estimated Construction Cost</div>
                  <div className="text-4xl md:text-5xl font-extrabold text-gold mt-2">₹{result.estimate.toLocaleString("en-IN")}</div>
                  <div className="mt-6 space-y-2 text-sm text-white/70">
                    <div className="flex justify-between border-b border-white/10 pb-2"><span>Package</span><span className="text-white font-semibold">{result.package}</span></div>
                    <div className="flex justify-between border-b border-white/10 pb-2"><span>Rate</span><span className="text-white font-semibold">₹{result.rate} / sq.ft.</span></div>
                    <div className="flex justify-between border-b border-white/10 pb-2"><span>Total Area</span><span className="text-white font-semibold">{result.total_area.toLocaleString("en-IN")} sq.ft.</span></div>
                  </div>
                  <p className="mt-5 text-xs text-white/50 leading-relaxed">{result.disclaimer}</p>
                  <Button to="/contact" variant="primary" className="w-full mt-6">Get Detailed Estimate</Button>
                </div>
              ) : (
                <div className="text-center py-12 text-white/50">
                  <CheckCircle2 size={40} className="mx-auto mb-4 text-white/20" />
                  <p>Enter your details and calculate to see an indicative construction cost estimate.</p>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
